<template>
  <section class="admin-content">
    <Navbar />
    <main class="admin-main">

      <!-- ── Page Header ─────────────────────────────────────── -->
      <div class="page-header">
        <div class="container">
          <div class="d-flex align-items-center text-white" style="padding: 32px 0;">
            <div class="header-icon-wrap mr-3">
              <i class="fe fe-user-plus" style="font-size: 22px;"></i>
            </div>
            <div>
              <h4 class="mb-0 font-weight-bold">Add Employee</h4>
              <small style="opacity: 0.8;">Register a new insurance, facility or establishment user</small>
            </div>
          </div>
        </div>
      </div>

      <div class="container mt-n3 pb-5">
        <form @submit.prevent="registerUser">

          <!-- ── 1. User Type ────────────────────────────────── -->
          <div class="card shadow-sm form-card mb-4">
            <div class="form-card-header">
              <div class="section-number">1</div>
              <h6 class="mb-0 font-weight-bold text-white">User Type</h6>
            </div>
            <div class="card-body px-4 pt-4 pb-3">
              <div class="form-row">
                <div class="form-group col-md-4">
                  <label class="small font-weight-bold text-secondary">
                    Select User Type <span class="text-danger">*</span>
                  </label>
                  <select class="form-control" v-model="register.type" required>
                    <option value="employee">Insurance User</option>
                    <option value="provider_employee">Facility User</option>
                    <option value="mda_employee">Establishment User</option>
                  </select>
                </div>

                <!-- Facility selector — only for provider_employee -->
                <div class="form-group col-md-4" v-if="register.type == 'provider_employee'">
                  <label class="small font-weight-bold text-secondary">
                    Select Facility <span class="text-danger">*</span>
                  </label>
                  <v-select v-model="register.institutional_id" :options="providers" label="agency_name"
                    placeholder="Search facility…" />
                </div>

                <!-- Ministry/organization selector — only for mda_employee -->
                <div class="form-group col-md-4" v-if="register.type == 'mda_employee'">
                  <label class="small font-weight-bold text-secondary">
                    Select Organization <span class="text-danger">*</span>
                  </label>
                  <v-select v-model="register.institutional_id" :options="ministries" label="name"
                    placeholder="Search organization…" />
                </div>
              </div>
            </div>
          </div>

          <!-- ── 2. Personal Information ─────────────────────── -->
          <div class="card shadow-sm form-card mb-4">
            <div class="form-card-header">
              <div class="section-number">2</div>
              <h6 class="mb-0 font-weight-bold text-white">Personal Information</h6>
            </div>
            <div class="card-body px-4 pt-4 pb-3">
              <div class="form-row">
                <div class="form-group col-md-4">
                  <label class="small font-weight-bold text-secondary">
                    First Name <span class="text-danger">*</span>
                  </label>
                  <input type="text" class="form-control" v-model="register.firstname" placeholder="First name"
                    required />
                </div>
                <div class="form-group col-md-4">
                  <label class="small font-weight-bold text-secondary">
                    Last Name <span class="text-danger">*</span>
                  </label>
                  <input type="text" class="form-control" v-model="register.lastname" placeholder="Last name"
                    required />
                </div>
                <div class="form-group col-md-4">
                  <label class="small font-weight-bold text-secondary">
                    Email Address <span class="text-danger">*</span>
                  </label>
                  <input type="email" class="form-control" v-model="register.email" placeholder="email@example.com"
                    required />
                </div>
                <div class="form-group col-md-4">
                  <label class="small font-weight-bold text-secondary">
                    Phone Number <span class="text-danger">*</span>
                  </label>
                  <input v-phone type="text" class="form-control" v-model="register.phone_number" placeholder="e.g. 08012345678"
                    required />
                </div>
              </div>
            </div>
          </div>

          <!-- ── 3. Role & Access (insurance employees only) ─── -->
          <div class="card shadow-sm form-card mb-4" v-if="register.type == 'employee'">
            <div class="form-card-header">
              <div class="section-number">3</div>
              <h6 class="mb-0 font-weight-bold text-white">Role &amp; Access</h6>
            </div>
            <div class="card-body px-4 pt-4 pb-3">
              <div class="form-row">
                <div class="form-group col-md-6">
                  <label class="small font-weight-bold text-secondary">
                    Job Title / Department <span class="text-danger">*</span>
                  </label>
                  <select class="form-control" v-model="register.job_title" required>
                    <option value="">Select department…</option>
                    <option :value="title" v-for="title in titles" :key="title">{{ title }}</option>
                  </select>
                </div>
                <div class="form-group col-md-6">
                  <label class="small font-weight-bold text-secondary">
                    Access Level <span class="text-danger">*</span>
                  </label>
                  <select class="form-control" v-model="register.role" required>
                    <option :value="admin_role">Admin</option>
                    <option :value="user_role">Maintainer</option>
                  </select>
                  <small class="text-muted">
                    Admins have full access. Maintainers have limited permissions.
                  </small>
                </div>
              </div>
            </div>
          </div>

          <!-- ── Default password notice ────────────────────── -->
          <div class="alert alert-notice d-flex align-items-center mb-4">
            <i class="fe fe-info mr-3" style="font-size: 1.1rem; flex-shrink: 0;"></i>
            <span>
              The default password for all new employees is <strong>'euhler'</strong>.
              Ask them to change it after their first login.
            </span>
          </div>

          <!-- ── Submit ─────────────────────────────────────── -->
          <div class="card shadow-sm border-0 form-card">
            <div class="card-body px-4 py-3 d-flex justify-content-end" style="gap: 10px;">
              <button type="button" class="btn btn-outline-secondary px-4" @click="$router.go(-1)">
                Cancel
              </button>
              <button type="submit" class="btn btn-submit px-5">
                <i class="fe fe-send mr-2"></i> Add Employee
              </button>
            </div>
          </div>

        </form>
      </div>

      <div class="vld-parent">
        <loading :active.sync="isLoading" loader="dots" :can-cancel="true" :is-full-page="fullPage"></loading>
      </div>
    </main>
  </section>
</template>

<script>
  import Navbar from "@/views/Navbar.vue";
  import Loading from "vue-loading-overlay";
  import "vue-loading-overlay/dist/vue-loading.css";

  export default {
    components: {
      Navbar,
      Loading,
    },
    data() {
      return {
        isLoading: false,
        fullPage: true,
        states: "",
        ministries: "",
        state: "",
        lga_states: "",
        wards: "",
        admin_role: 1,
        user_role: 0,
        titles: [
          "Executive Secretary",
          "Claims Vetter",
          "Claims Verifier",
          "Authorization Code",
          "Tickets",
          "Zonal Coordinator",
          "Operations",
          "Enrollment",
          "Enrollment_BHCPF",
          "Referral Provider",
          "Marketer",
          "dashboard",
          "M and E",
          "ICT",
          "Finance",
          "Inter-state",
        ],
        providers: "",
        register: {
          firstname: "",
          lastname: "",
          email: "",
          phone_number: "",
          type: "employee",
          state: "",
          institutional_id: "",
          job_title: "",
          lga: "",
          id_card_number: "",
          ward: "",
          address: "",
          role: 0,
        },
      };
    },
    beforeMount() {
      this.user = JSON.parse(localStorage.getItem("user"));
    },
    methods: {
      fetchWards() {
        this.axios
          .get(`/api/v1/auth/getwards/${event.target.value}`)
          .then((response) => {
            this.wards = response.data.data;
            console.log(response);
          })
          .catch((error) => {
            console.error(error);
          });
      },

      registerUser() {
        this.user = JSON.parse(localStorage.getItem("user"));
        this.isLoading = true;
        this.axios
          .post("/api/v1/auth/registerProvider", {
            firstname: this.register.firstname,
            lastname: this.register.lastname,
            email: this.register.email,
            phone_number: this.register.phone_number,
            type: "employee",
            password: "euhler",
            state: 2679,
            enrolled_by: 0,
            localgovt: this.register.localgovt,
            ward: this.register.ward,
            id_card_number: this.register.id_card_number,
            institutional_id: this.register.type == 'employee' ? 407465 : this.register.institutional_id.id,
            job_title: this.register.job_title,
            user_role: this.register.role,
          })
          .then((response) => {
            console.log(response);
            this.isLoading = false;
            this.register = {
              firstname: "", lastname: "", email: "", phone_number: "",
              type: "employee", institutional_id: "", job_title: "", role: 0,
            };
            this.$toasted.success("Employee added successfully!", {
              position: "top-center",
              duration: 3000,
            });
            this.$toasted.info("Default password is 'euhler'", {
              position: "top-center",
              duration: 4000,
            });
          })
          .catch((error) => {
            console.log(error.response);
            this.isLoading = false;
            let response = error.response.data.errors;
            this.$toasted.error(`${JSON.stringify(response)}`, {
              position: "top-center",
              duration: 3000,
            });
          });
      },

      getProviders() {
        this.user = JSON.parse(localStorage.getItem("user"));
        this.axios
          .get(`/api/v1/auth/providerAgency/407465`)
          .then((response) => {
            this.providers = response.data.data;
            console.log(response);
          })
          .catch((error) => {
            console.error(error);
          });
      },

      getDepts() {
        this.axios
          .get(`/api/v1/auth/ministry`, {
            params: {
              type: this.type,
              agency_id: 407465,
            },
          })
          .then((response) => {
            this.ministries = response.data.data;
            console.log(response);
          })
          .catch((error) => {
            console.error(error);
          });
      },
    },
    created() {
      this.getProviders();
      this.getDepts();
    },
  };
</script>

<style scoped>
  /* ── Page Header ─────────────────────────────────────── */
  .page-header {
    background: linear-gradient(135deg, #1e3a5f 0%, #2563eb 100%);
    padding-bottom: 60px;
  }

  .header-icon-wrap {
    width: 48px;
    height: 48px;
    background: rgba(255, 255, 255, 0.18);
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  /* ── Form Cards ──────────────────────────────────────── */
  .form-card {
    border-radius: 14px;
    border: none;
    overflow: hidden;
  }

  .form-card-header {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 16px 24px;
    background: linear-gradient(135deg, #1e3a5f, #2563eb);
  }

  .section-number {
    width: 28px;
    height: 28px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.25);
    color: #fff;
    font-size: 13px;
    font-weight: 700;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  /* ── Submit Button ───────────────────────────────────── */
  .btn-submit {
    background: linear-gradient(135deg, #2563eb, #7c3aed);
    color: #fff;
    border: none;
    border-radius: 8px;
    font-weight: 600;
  }

  .btn-submit:hover {
    opacity: 0.92;
    color: #fff;
  }

  /* ── Notice alert ────────────────────────────────────── */
  .alert-notice {
    background: #eff6ff;
    border: 1px solid #bfdbfe;
    color: #1e40af;
    border-radius: 10px;
    font-size: 0.88rem;
  }

  /* ── Form controls ───────────────────────────────────── */
  .form-control:focus {
    border-color: #2563eb;
    box-shadow: 0 0 0 0.15rem rgba(37, 99, 235, 0.2);
  }
</style>
